import { ref, watch, onMounted } from 'vue';
import { useContextStore } from '@/stores/context';

/**
 * Hook quản lý theme của ứng dụng
 * Thay đổi theme được áp dụng ngay lập tức mà không cần refresh trang
 * Theme được lưu trong localStorage với key theme_{UserId}
 * 
 * @returns {Object} theme - Đối tượng theme hiện tại
 * @returns {Function} toggleTheme - Hàm chuyển đổi giữa các theme
 * @returns {Function} setTheme - Hàm đặt theme cụ thể
 */
export default function useTheme() {
  const contextStore = useContextStore();
  const theme = ref('blue'); // Giá trị mặc định
  
  // Các theme được hỗ trợ
  const availableThemes = ['blue', 'green'];
  
  /**
   * Lấy key theme trong localStorage dựa vào UserId
   * @returns {String} key theme trong localStorage
   */
  const getThemeKey = () => {
    const userId = contextStore.Context?.UserId;
    return userId ? `theme_${userId}` : 'theme';
  };

  /**
   * Áp dụng theme vào document
   * @param {String} newTheme - Theme để áp dụng
   */
  const applyTheme = (newTheme) => {
    // Lưu theme trong localStorage với key theo userId
    localStorage.setItem(getThemeKey(), newTheme);
    
    // Đặt data-theme attribute cho thẻ html
    document.documentElement.setAttribute('data-theme', newTheme);
    
    // Cập nhật giá trị theme
    theme.value = newTheme;
  };
  
  /**
   * Chuyển đổi giữa các theme
   */
  const toggleTheme = () => {
    const currentIndex = availableThemes.indexOf(theme.value);
    const nextIndex = (currentIndex + 1) % availableThemes.length;
    applyTheme(availableThemes[nextIndex]);
  };
  
  /**
   * Đặt theme cụ thể
   * @param {String} newTheme - Theme để đặt
   */
  const setTheme = (newTheme) => {
    if (availableThemes.includes(newTheme)) {
      applyTheme(newTheme);
    } else {
      console.warn(`Theme "${newTheme}" không được hỗ trợ. Các theme được hỗ trợ: ${availableThemes.join(', ')}`);
    }
  };
  const initTheme = async () => {
    // Đọc từ localStorage theo key theme_{UserId}
    const themeKey = getThemeKey();
    const storedTheme = localStorage.getItem(themeKey) || 'blue';
    
    // Áp dụng theme từ localStorage ngay lập tức cho hiệu suất
    theme.value = storedTheme;
    document.documentElement.setAttribute('data-theme', storedTheme);
    
    // Kiểm tra nếu có theme trong localStorage với key cũ (theme) nhưng không có trong key mới (theme_{UserId})
    // Điều này sẽ xảy ra khi lần đầu tiên người dùng chạy sau khi cập nhật code
    if (!localStorage.getItem(themeKey) && localStorage.getItem('theme')) {
      const oldTheme = localStorage.getItem('theme');
      if (availableThemes.includes(oldTheme)) {
        // Di chuyển theme từ key cũ sang key mới
        localStorage.setItem(themeKey, oldTheme);
        theme.value = oldTheme;
        document.documentElement.setAttribute('data-theme', oldTheme);
        // Giữ lại key cũ cho backward compatibility
      }
    }else{
      // Nếu không có theme trong localStorage, đặt theme mặc định
      localStorage.setItem(themeKey, 'blue');
      theme.value = 'blue';
      document.documentElement.setAttribute('data-theme', 'blue');
    }
  }
  // Áp dụng theme khi component được mount
  onMounted(async () => {
    
    
    // Thêm phần tử style vào head để ngăn nhấp nháy khi chuyển theme
    //const styleEl = document.createElement('style');
    // styleEl.textContent = `
    //   /* Ngăn nhấp nháy khi chuyển theme bằng cách kích hoạt GPU */
    //   html, body {
    //     backface-visibility: hidden;
    //     -webkit-backface-visibility: hidden;
    //     -moz-backface-visibility: hidden;
    //     -ms-backface-visibility: hidden;
    //     transform: translateZ(0);
    //     -webkit-transform: translateZ(0);
    //     -moz-transform: translateZ(0);
    //     -ms-transform: translateZ(0);
    //   }
    // `;
    //document.head.appendChild(styleEl);
  });
  
  return {
    theme,
    toggleTheme,
    setTheme,
    initTheme
  };
}
